import { useDisclosure } from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useManualClaimMutation } from "../../mutations/bridge";
import { l2AccountAddressQuery } from "../../queries/aztec";
import type { ManualClaimParams, ManualClaimTx } from "../../state/types";
import { ManualClaimInitialModal } from "./ManualClaimInitialModal";
import { ManualClaimParamsModal } from "./ManualClaimParamsModal";
import { ManualClaimResultModal } from "./ManualClaimResultModal";

export function ManualClaimModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { data: l2AccountAddress } = useQuery(l2AccountAddressQuery);

  const [claimNetwork, setClaimNetwork] = useState<"l1" | "l2">("l1");

  const initialModalDisclosure = useDisclosure({ isOpen });
  const paramsModalDisclosure = useDisclosure();
  const successModalDisclosure = useDisclosure();
  const failModalDisclosure = useDisclosure();

  const manualClaimMutation = useManualClaimMutation({
    onSuccess: (claimTx) => {
      claimTxRef.current = claimTx;
      paramsModalDisclosure.onClose();
      successModalDisclosure.onOpen();
    },
    onError: (claimTx) => {
      claimTxRef.current = claimTx;
      paramsModalDisclosure.onClose();
      failModalDisclosure.onOpen();
    },
  });

  const claimParamsRef = useRef<ManualClaimParams | null>(null);
  const claimTxRef = useRef<ManualClaimTx | null>(null);

  function handleClaim(claimParams: ManualClaimParams) {
    claimParamsRef.current = claimParams;
    manualClaimMutation.mutate(claimParams);
  }

  function handleRetry() {
    if (!claimParamsRef.current) {
      console.error("No claim parameters found! Failed to retry.");
      return;
    }
    claimTxRef.current = null;
    manualClaimMutation.mutate(claimParamsRef.current);
  }

  return (
    <>
      <ManualClaimInitialModal
        isOpen={initialModalDisclosure.isOpen}
        onClose={onClose}
        onContinue={() => {
          initialModalDisclosure.onClose();
          paramsModalDisclosure.onOpen();
        }}
        claimNetwork={claimNetwork}
        setClaimNetwork={setClaimNetwork}
      />

      <ManualClaimParamsModal
        isOpen={paramsModalDisclosure.isOpen}
        onBack={paramsModalDisclosure.onClose}
        onClose={() => {
          paramsModalDisclosure.onClose();
          onClose();
        }}
        claimType={claimNetwork === "l1" ? "deposit" : "withdraw"}
        recipientAddress={l2AccountAddress?.toString()}
        onClaim={handleClaim}
      />

      <ManualClaimResultModal
        isOpen={successModalDisclosure.isOpen}
        onClose={successModalDisclosure.onClose}
        onRetry={handleRetry}
        claimTx={claimTxRef.current}
      />
    </>
  );
}
